<template>
  <div>
    <!-- <pre v-if="true" class="p-2 mb-2 text-xs bg-gray-100">
      Component: {{ widget.component }}
      Is Active: {{ isActive }}
      Resolved: {{ !!resolvedComponent }}
      Clean Name: {{ getCleanComponentName(widget.component) }}
    </pre> -->

    <component
      v-if="resolvedComponent && isActive"
      :is="resolvedComponent"
      v-bind="componentProps"
    />
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, inject } from "vue";

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

// Fungsi untuk mendapatkan nama komponen murni - define outside of computed for reuse
const getCleanComponentName = (path) => {
  if (!path) return "";

  let name = path;

  // Jika ada slash, ambil bagian terakhir saja
  if (name.includes("/")) {
    name = name.split("/").pop();
  }

  // Hapus prefiks 'Web/' jika ada
  if (name.startsWith("Web/")) {
    name = name.substring(4);
  }

  // Hapus ekstensi .vue jika ada
  if (name.endsWith(".vue")) {
    name = name.slice(0, -4);
  }

  return name;
};

// Resolve component path dynamically
const resolvedComponent = ref(null);
const isActive = computed(() => {
  console.log("Widget active status:", props.widget.is_active);
  // Be more lenient with boolean type conversion
  return (
    props.widget.is_active === true ||
    props.widget.is_active === 1 ||
    props.widget.is_active === "1" ||
    props.widget.is_active === "true"
  );
});

// Inject all needed data from parent component
const formasi = inject("formasi", {});
const news = inject("news", {});
const slider = inject("slider", {});
const agenda = inject("agenda", []);
const grafik = inject("grafik", []);
const jenisdokumen = inject("jenisdokumen", {});
const pemilihan = inject("pemilihan", []);
const pengumuman = inject("pengumuman", []);

// Log data yang diinject untuk debugging
// console.log("Injected data:");
// console.log("- formasi:", formasi);
// console.log("- news:", news);
// console.log("- slider:", slider);
// console.log("- agenda:", typeof agenda, agenda);
// console.log("- grafik:", grafik);
// console.log("- jenisdokumen:", jenisdokumen);

// Determine which props to pass based on component type
const componentProps = computed(() => {
  // Start with the widget config as base props
  const baseProps = props.widget.config || {};

  // Ambil nama komponen dari jalur
  const componentPath = props.widget.component || "";
  const componentName = getCleanComponentName(componentPath);

  // console.log("Nama komponen untuk props:", componentName);

  // // Tentukan props berdasarkan nama komponen
  // console.log("Mencoba mencocokkan komponen:", componentName);

  // Log semua komponen yang mungkin untuk debugging
  // console.log("Komponen yang tersedia untuk pencocokan:");
  // console.log("- Carousel");
  // console.log("- BlogSection");
  // console.log("- AuthorSection");
  // console.log("- AgendaSection");
  // console.log("- GraphSection");
  // console.log("- JurusanStats");
  // console.log("- LayananStats");
  // console.log("- DokumenList");

  switch (componentName) {
    case "Carousel":
      console.log("Terdeteksi Carousel, passing slider props:", slider);
      return { ...baseProps, slider };
    case "HeroSection":
      console.log("Terdeteksi HeroSection, passing slider props");
      return { ...baseProps, slider };
    case "StatsBand":
      console.log("Terdeteksi StatsBand, passing stats data props");
      return { ...baseProps, news, agenda, formasi, jenisdokumen };
    case "BlogSection":
      console.log("Terdeteksi BlogSection, passing news props");
      return { ...baseProps, news };
    case "AuthorSection":
      console.log("Terdeteksi AuthorSection, passing formasi props");
      return { ...baseProps, formasi };
    case "AgendaSection":
      console.log("Terdeteksi AgendaSection, passing agenda props");
      return { ...baseProps, agenda };
    case "GraphSection":
      console.log("Terdeteksi GraphSection, passing grafik props");
      return { ...baseProps, grafik };
    case "JurusanStats":
      console.log("Terdeteksi JurusanStats, passing jurusan props");
      return { ...baseProps, jurusan: props.widget.jurusan || [] };
    case "LayananStats":
      console.log("Terdeteksi LayananStats, passing layanan props");
      return { ...baseProps, layanan: props.widget.layanan || [] };
    case "DokumenList":
      console.log(
        "Terdeteksi DokumenList, passing jenisdokumen props:",
        jenisdokumen
      );
      return { ...baseProps, jenisdokumen };
    case "PemilihanSection":
      console.log("Terdeteksi PemilihanSection, passing pemilihan props");
      return { ...baseProps, pemilihan };
    case "RunningText":
      console.log("Terdeteksi RunningText, passing pengumuman props");
      return { ...baseProps, pengumuman };
    // Tambahkan komponen lain sesuai kebutuhan
    default:
      console.log(
        "Tidak ada jenis komponen yang cocok, menggunakan props default untuk:",
        componentName
      );
      return baseProps;
  }
});

onMounted(() => {
  // Ekstrak jalur komponen dari widget
  const componentPath = props.widget.component;
  console.log("Data widget:", props.widget);
  console.log("Jalur komponen dari database:", componentPath);

  if (componentPath) {
    // Coba impor komponen secara dinamis
    try {
      // Gunakan fungsi yang sama untuk mendapatkan nama komponen konsisten
      const rawComponentName = getCleanComponentName(componentPath);
      console.log("Nama komponen tanpa ekstensi:", rawComponentName);

      // Gunakan pendekatan yang lebih sederhana dengan defineAsyncComponent
      resolvedComponent.value = defineAsyncComponent({
        // Set options
        delay: 200,
        timeout: 5000,

        // Loader function
        loader: () => {
          console.log(`Memuat komponen: ${rawComponentName}`);

          // Create a mapping of component names to their import paths
          const componentMapping = {
            Carousel: () => import("../Carousel.vue"),
            HeroSection: () => import("../HeroSection.vue"),
            StatsBand: () => import("../StatsBand.vue"),
            AuthorSection: () => import("../AuthorSection.vue"),
            AgendaSection: () => import("../AgendaSection.vue"),
            BlogSection: () => import("../BlogSection.vue"),
            GraphSection: () => import("../GraphSection.vue"),
            VisitorStats: () => import("../VisitorStats.vue"),
            JurusanStats: () => import("../JurusanStats.vue"),
            LayananStats: () => import("../LayananStats.vue"),
            DokumenList: () => import("../DokumenList.vue"),
            PemilihanSection: () => import("../PemilihanSection.vue"),
            RunningText: () => import("../RunningText.vue"),
          };

          // Check if we have a direct mapping for this component
          if (componentMapping[rawComponentName]) {
            console.log(
              `Menggunakan import eksplisit untuk ${rawComponentName}`
            );
            return componentMapping[rawComponentName]();
          }

          // Fallback to dynamic import using relative path
          console.log(`Menggunakan import dinamis untuk ${rawComponentName}`);
          return import(`../${rawComponentName}.vue`).catch((error) => {
            console.error(
              `Gagal memuat dengan jalur ../${rawComponentName}.vue, mencoba jalur alternatif`,
              error
            );

            // Try alternative path (components might be in different locations)
            return import(`@/Components/Web/${rawComponentName}.vue`).catch(
              (error2) => {
                console.error(
                  `Gagal memuat dengan jalur @/Components/Web/${rawComponentName}.vue`,
                  error2
                );
                throw error2;
              }
            );
          });
        },

        // Error handling
        onError(error) {
          console.error(
            `Failed to load widget component: ${componentPath}`,
            error
          );
        },
      });
    } catch (error) {
      console.error(
        `Error setting up async component: ${componentPath}`,
        error
      );
    }
  } else {
    console.error("No component path provided for widget:", props.widget);
  }
});
</script>
